import * as React from "react";

export type StatusIconName =
  | "Not Favorable"
  | "Favorable and as Expected"
  | "Favorable"
  | "Out of Scope"
  | "Not addressed"
  | "Measured Differently";

export type StatusIconProps = React.SVGProps<SVGSVGElement> & {
  size?: number | string;
  title?: string;
};

const iconDefaults = {
  size: 24,
  strokeWidth: 2,
} as const;

function SvgBase({
  size = iconDefaults.size,
  title,
  children,
  ...props
}: StatusIconProps & { children: React.ReactNode }) {
  const titleId = React.useId();

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={title ? "img" : "presentation"}
      aria-labelledby={title ? titleId : undefined}
      aria-hidden={title ? undefined : true}
      {...props}
    >
      {/* {title ? <title id={titleId}>{title}</title> : null} */}
      {children}
    </svg>
  );
}

export function NotFavorableIcon({
  color = "#D92D20",
  strokeWidth = 5,
  ...props
}: StatusIconProps) {
  return (
    <SvgBase {...props}>
      <circle cx="36" cy="36" r="27" stroke={color} strokeWidth={strokeWidth} />
      <path
        d="M26 26L46 46M46 26L26 46"
        stroke={color}
        strokeWidth={6}
        strokeLinecap="round"
      />
    </SvgBase>
  );
}

export function FavorableAndAsExpectedIcon({
  color = "#2E7D32",
  strokeWidth = 5,
  ...props
}: StatusIconProps) {
  return (
    <SvgBase {...props}>
      <path
        d="M36 8L58 17V32C58 47 48.5 59.5 36 64C23.5 59.5 14 47 14 32V17L36 8Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path
        d="M25 36L32.5 43.5L48 28"
        stroke={color}
        strokeWidth={6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SvgBase>
  );
}

export function FavorableIcon({
  color = "#12B76A",
  strokeWidth = 5,
  ...props
}: StatusIconProps) {
  return (
    <SvgBase {...props}>
      <path
        d="M26 62H17C14.8 62 13 60.2 13 58V34C13 31.8 14.8 30 17 30H26V62Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path
        d="M26 32L37 14C39 10.8 44 12.2 44 16V29H54C57.8 29 60.7 32.5 60 36.2L56.5 54.2C55.8 58.7 51.9 62 47.3 62H26V32Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    </SvgBase>
  );
}

export function OutOfScopeIcon({
  color = "#667085",
  strokeWidth = 5,
  ...props
}: StatusIconProps) {
  return (
    <SvgBase {...props}>
      <circle cx="36" cy="36" r="27" stroke={color} strokeWidth={strokeWidth} />
      <path
        d="M17 55L55 17"
        stroke={color}
        strokeWidth={6}
        strokeLinecap="round"
      />
    </SvgBase>
  );
}

export function NotAddressedIcon({
  color = "#F79009",
  strokeWidth = 5,
  ...props
}: StatusIconProps) {
  return (
    <SvgBase {...props}>
      <circle cx="36" cy="36" r="27" stroke={color} strokeWidth={strokeWidth} />
      <path
        d="M27.5 29C28.2 23.8 32.1 21 36.9 21C42.3 21 46 24.4 46 29.1C46 36.8 36 36.4 36 45"
        stroke={color}
        strokeWidth={5.5}
        strokeLinecap="round"
      />
      <circle cx="36" cy="53" r="3.5" fill={color} />
    </SvgBase>
  );
}

export function MeasuredDifferentlyIcon({
  color = "#7A5AF8",
  strokeWidth = 5,
  ...props
}: StatusIconProps) {
  return (
    <SvgBase {...props}>
      <path
        d="M13 22H24C31.5 22 36 29 36 36C36 43 40.5 50 48 50H59"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path
        d="M13 50H24C27.5 50 30.2 48.5 32.2 46"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path
        d="M39.8 26C41.8 23.5 44.5 22 48 22H59"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path
        d="M52 15L59 22L52 29"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M52 43L59 50L52 57"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SvgBase>
  );
}

export const statusIconMap = {
  "Not Favorable": NotFavorableIcon,
  "Favorable and as Expected": FavorableAndAsExpectedIcon,
  Favorable: FavorableIcon,
  "Out of Scope": OutOfScopeIcon,
  "Not addressed": NotAddressedIcon,
  "Measured Differently": MeasuredDifferentlyIcon,
} satisfies Record<StatusIconName, React.ComponentType<StatusIconProps>>;

export function StatusIcon({
  name,
  ...props
}: StatusIconProps & { name: StatusIconName }) {
  const Icon = statusIconMap[name];
  return <Icon title={props.title ?? name} {...props} />;
}

/**
 * Example:
 *
 * <StatusIcon name="Not Favorable" size={32} />
 * <FavorableIcon size={20} color="currentColor" />
 */
